/** Import (onboarding) calls. Shapes copied from workers/api/src/onboarding (routes.ts, store.ts, report.ts, readiness.ts) as merged in #39. */
import type { ColumnMapping, EntityName, Mapping } from "@helmr/importer";
import type { ApiClient } from "./client";

export type { ColumnMapping, EntityName, Mapping };

export type ImportStatus = "draft" | "validated" | "dry_run_passed" | "dry_run_failed" | "approved" | "cutting_over" | "done" | "failed" | "cancelled";

export interface OpFailure { seq: number; type: string; key: string; message: string }

/** `import_jobs.counts`: written by validate, extended by a dry run and by cutover (`applied`). */
export interface JobCounts {
  plan: Record<string, number>;
  skippedRows: number;
  errors: number;
  warnings: number;
  readback?: Record<string, number>;
  failureCount?: number;
  failures?: OpFailure[];
  applied?: number;
}

export interface ImportFile { id: string; filename: string; entity: EntityName; rowCount: number; sha256: string; uploadedAt: number }

export interface ImportJob {
  id: string;
  status: ImportStatus;
  label: string;
  onError: "skip_rows" | "abort";
  defaultBranchId: string;
  planKey: string | null;
  signedOffAt: number | null;
  signedOffByPersonId: string | null;
  scheduledFor: string | null;
  counts: JobCounts | null;
  error: string | null;
  filesExpireAt: number | null;
  /** The server guards concurrent edits with this (a stale write answers 409 `conflict`). */
  version: number;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  files: ImportFile[];
  mappedFileIds: string[];
}

export interface OwnerReport {
  today: string;
  canProceed: boolean;
  blocked: boolean;
  blockedReason: string | null;
  onError: "skip_rows" | "abort";
  planKey: string;
  operations: Record<string, number>;
  skipped: { total: number; rows: { file: string; row: number; entity: string; disposition: string; codes: string[] }[] };
  errors: { total: number; byCode: Record<string, number> };
  warnings: { total: number; byCode: Record<string, number> };
  ambiguousDates: { total: number; columns: { file: string; column: string; field: string; entity: string; count: number }[] };
  duesTotalPaise: number;
  amountPaidPaise: number;
  plansEndingWithin30Days: number;
  deviceMappings: number;
  unmappedPlanNames: { entity: "memberships" | "pt_balances"; name: string; rows: number }[];
  unmappedRoleNames: { name: string; rows: number }[];
  issues: { file: string; row: number; entity: string; severity: string; code: string; field: string | null; message: string }[];
  issuesTruncated: boolean;
}

export interface MappingCheck { ok: boolean; missingRequired: { entity: string; field: string }[]; duplicateTargets: { entity: string; field: string }[]; unknownTargets: { entity: string; field: string }[] }
export interface Suggestion { fileId: string; filename: string; mapping: Mapping; check: MappingCheck }
export interface ReadinessCheck { key: string; label: string; state: "ok" | "todo" | "info"; detail: string }
export interface PlanItemBody { entity: "memberships" | "pt_balances"; sourceName: string; itemId?: string; create?: { name?: string; price?: number; durationDays: number; sessionsTotal?: number } }

const base = (id: string) => `/imports/${id}`;

export const listImports = (c: ApiClient) => c.get<{ jobs: ImportJob[] }>("/imports").then((r) => r.jobs);
export const getImport = (c: ApiClient, id: string) => c.get<{ job: ImportJob }>(base(id)).then((r) => r.job);
export const createImport = (c: ApiClient, body: { label: string; defaultBranchId: string; onError?: "skip_rows" | "abort" }) =>
  c.post<{ job: ImportJob }>("/imports", body).then((r) => r.job);

/** A sheet as text cells, sent as JSON (the client parses CSV and XLSX; the Worker accepts rows). */
export const uploadSheet = (c: ApiClient, id: string, body: { filename: string; entity: EntityName; rows: string[][] }) =>
  c.post<{ job: ImportJob }>(`${base(id)}/files`, body).then((r) => r.job);

export const suggestMapping = (c: ApiClient, id: string, fileId?: string) =>
  c.post<{ suggestions: Suggestion[] }>(`${base(id)}/mapping/suggest`, fileId ? { fileId } : {}).then((r) => r.suggestions);
export const saveMapping = (c: ApiClient, id: string, fileId: string, mapping: Mapping) =>
  c.request<{ job: ImportJob; check: MappingCheck }>("PUT", `${base(id)}/mapping`, { body: { fileId, mapping } });
export const savePlanItems = (c: ApiClient, id: string, items: PlanItemBody[]) =>
  c.request<{ job: ImportJob }>("PUT", `${base(id)}/plan-items`, { body: { items } });
export const saveRoles = (c: ApiClient, id: string, roles: { sourceName: string; roleId: string }[]) =>
  c.request<{ job: ImportJob }>("PUT", `${base(id)}/roles`, { body: { roles } });

export const validateImport = (c: ApiClient, id: string) => c.post<{ job: ImportJob; report: OwnerReport }>(`${base(id)}/validate`, {});
export const getReport = (c: ApiClient, id: string) => c.get<{ status: ImportStatus; report: OwnerReport }>(`${base(id)}/report`);
export const dryRun = (c: ApiClient, id: string) => c.post<{ job: ImportJob; passed: boolean; counts: JobCounts; report: OwnerReport }>(`${base(id)}/dry-run`, {});
export const signOff = (c: ApiClient, id: string, body: { planKey: string; acceptedWarnings: boolean }) => c.post<{ job: ImportJob }>(`${base(id)}/sign-off`, body);
/** The caller passes the key so a retry of the same intent replays the stored 202 instead of starting twice. */
export const startCutover = (c: ApiClient, id: string, idempotencyKey: string) =>
  c.post<{ job: ImportJob; resumed: boolean }>(`${base(id)}/cutover`, undefined, { idempotencyKey });
export const cancelImport = (c: ApiClient, id: string) => c.post<{ job: ImportJob }>(`${base(id)}/cancel`, {}).then((r) => r.job);
export const getReadiness = (c: ApiClient, id: string) => c.get<{ status: ImportStatus; checks: ReadinessCheck[]; ready: boolean }>(`${base(id)}/readiness`);
